import { IProjectMilestoneProgress } from '@lb/api/mylb/interfaces/lesson/ilesson-detail.interface';

/**
 * Project progress is stored per lesson (`Lesson.LessonProjectProgress`), but the payload it is
 * hydrated from — `semester.projectDetail.projectProgress` — covers every lesson in the semester.
 * Everything here narrows that semester-wide list down to a single lesson, so the read path (which
 * milestone does this lesson show?) and the write path (which milestone does this save send?) can
 * never disagree about what "already saved" means.
 *
 * They used to disagree, and that was the bug: the write path diffed the selection against the
 * semester's whole set of recorded milestones, so re-selecting a milestone another lesson had
 * already reached sent an empty array. The backend only reconciles when the array is non-empty, so
 * the lesson completed with no progress row at all.
 */

/** Every milestone recorded against this lesson. Normally 0 or 1; more only from legacy rows. */
export function getSavedMilestoneIDsForLesson(savedProgress: IProjectMilestoneProgress[], lessonID: number | null): number[] {
	if (lessonID == null) {
		return [];
	}

	return savedProgress.filter((p) => p.lessonID === lessonID).map((p) => p.projectMilestoneID);
}

/**
 * The milestone this lesson currently shows in the dropdown, or null when it has none recorded.
 * Scoped to the lesson on purpose: a milestone reached on a *different* lesson of the same semester
 * is not this lesson's selection.
 */
export function getSavedMilestoneForLesson(savedProgress: IProjectMilestoneProgress[], lessonID: number | null): number | null {
	return getSavedMilestoneIDsForLesson(savedProgress, lessonID)[0] ?? null;
}

/**
 * The `projectMilestones` array to put on a save request.
 *
 * The backend treats a non-empty array as authoritative for the lesson — it deletes the lesson's
 * rows and re-inserts — and skips the table entirely when the array is empty. So this returns empty
 * only when the stored state is *already exactly* the selection: one row, for this lesson, on this
 * milestone. Anything else (nothing stored, a different milestone stored, or several stale rows
 * stored) sends the selection and lets the reconcile settle it, which also cleans up any lesson that
 * accumulated duplicate rows before the reconcile existed.
 */
export function resolveMilestonesToSave(selectedMilestoneID: number | null, savedProgress: IProjectMilestoneProgress[], lessonID: number | null): number[] {
	if (selectedMilestoneID == null) {
		return [];
	}

	const saved = getSavedMilestoneIDsForLesson(savedProgress, lessonID);
	const alreadyStoredExactly = saved.length === 1 && saved[0] === selectedMilestoneID;

	return alreadyStoredExactly ? [] : [selectedMilestoneID];
}
